import type { LoginRequest } from '@sentra/contracts';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isApiError } from '@/api/errors';
import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { useSelfServiceReset } from '@/app/providers/config';
import { Alert, AlertDescription } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { AuthCard } from '@/features/auth/auth-card';
import type { ResetPurpose } from '@/features/auth/reset';
import { ResetFlow } from '@/features/auth/reset';
import { useAnonymousCsrfToken, useSessionContext } from '@/features/auth/session';

/**
 * Signing in, including the second factor and the recovery code beside it.
 *
 * **The second factor is a step, not a field on the first screen.** `security.totp_required_admins`
 * ships `true`, so every administrator meets it; an operator usually does not. Asking everybody for
 * a code they may not have would teach them to ignore the field. So the password is submitted
 * first, and `AUTH_TOTP_REQUIRED` is what opens the second step — which is also the only honest
 * source for whether this account has a second factor, because asking the server before a password
 * has been proved would be an account-enumeration oracle.
 *
 * **The two second factors are alternatives.** `loginRequestSchema` refuses both at once and says
 * why: the service would otherwise need a precedence nobody chose. The form offers one or the
 * other and sends exactly what was filled in.
 *
 * **A refusal is rendered from its code**, so the message follows the operator's own language
 * rather than whatever the server negotiated, and a locked account says how long it is locked for
 * because that number travels in the server's rendering of the same key.
 */
export function LoginPage(): ReactNode {
  const { t } = useTranslation();
  const [onSecondStep, setOnSecondStep] = useState(false);
  const [resetting, setResetting] = useState<ResetPurpose | null>(null);
  const offersReset = useSelfServiceReset();

  if (resetting !== null) {
    return (
      <AuthCard
        title={t(resetting === 'password' ? 'auth.reset.passwordTitle' : 'auth.reset.totpTitle')}
        description={t('auth.reset.subtitle')}
      >
        <ResetFlow
          purpose={resetting}
          onBack={() => {
            setResetting(null);
          }}
        />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t(onSecondStep ? 'auth.secondFactorTitle' : 'auth.signInTitle')}
      description={t(onSecondStep ? 'auth.secondFactorSubtitle' : 'auth.signInSubtitle')}
    >
      <LoginForm onStepChange={setOnSecondStep} />
      {/*
        **The two links live on the PAGE and not in the form, and that placement is the rule.**
        `LoginForm` is rendered in three places: here, the re-authentication overlay over an expired
        session, and the hand-over dialog. The other two mean somebody is already standing at a live
        terminal, and offering a way to restore a credential from a locked screen would be a way
        past the lock. Only the sign-in screen may show them.

        They are also hidden entirely unless the installation can actually send the message, so a
        control that would dead-end is never drawn.
      */}
      {offersReset && !onSecondStep && (
        <div className="flex flex-col items-start gap-1 border-t pt-4">
          <Button
            type="button"
            variant="link"
            className="px-0"
            data-testid="reset-password-link"
            onClick={() => {
              setResetting('password');
            }}
          >
            {t('auth.reset.forgotPassword')}
          </Button>
          <Button
            type="button"
            variant="link"
            className="px-0"
            data-testid="reset-totp-link"
            onClick={() => {
              setResetting('totp');
            }}
          >
            {t('auth.reset.lostAuthenticator')}
          </Button>
        </div>
      )}
    </AuthCard>
  );
}

/**
 * The form alone, without the card around it.
 *
 * It is separated because the same form is needed in three places that cannot share a layout: the
 * sign-in screen, the re-authentication overlay that appears OVER an operator's in-progress work
 * when their session has expired outright, and the hand-over dialog. Rendering the page inside
 * either would put a full-height centred card inside a dialog; rendering a second copy of the form
 * would be three places to keep the second-factor logic correct.
 *
 * **The ROUTE is a parameter, and that is the whole of what makes a hand-over different from a
 * sign-in.** `auth.switch_operator` takes the same body and answers the same shape, but it destroys
 * the outgoing session id in the same `regenerate` that mints the new one and records a different
 * audit action — so the shift that ends and the shift that begins can never be attributed to one
 * another. Pointing this form at it is therefore the correct implementation of AD-54's explicit
 * hand-over, and a separate "just end this session" call would have been the wrong one.
 */
export function LoginForm({
  route = apiRoutes.authLogin,
  onStepChange,
  onAuthenticated,
}: Readonly<{
  route?: typeof apiRoutes.authLogin | typeof apiRoutes.authSwitchOperator;
  onStepChange?: (onSecondStep: boolean) => void;
  onAuthenticated?: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const { client, adopt } = useSessionContext();
  const describe = useApiErrorMessage();
  useAnonymousCsrfToken();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [secondFactor, setSecondFactor] = useState<'none' | 'totp' | 'recovery'>('none');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);

  function body(): LoginRequest {
    return {
      email,
      password,
      ...(secondFactor === 'totp' && totpCode.length > 0 ? { totpCode } : {}),
      ...(secondFactor === 'recovery' && recoveryCode.length > 0 ? { recoveryCode } : {}),
    };
  }

  async function submit(): Promise<void> {
    setFailure(null);
    setSubmitting(true);
    try {
      const answer = await client.request(route, { body: body() });
      setRemaining(answer.recoveryCodesRemaining);
      adopt(answer.session);
      onAuthenticated?.();
    } catch (error) {
      if (isApiError(error) && error.code === 'AUTH_TOTP_REQUIRED') {
        // The account has a second factor. This is the first moment anybody could know that, and
        // the first moment it is safe to say so: the password has already been proved.
        setSecondFactor('totp');
        setFailure(null);
        return;
      }
      setFailure(describe(error));
    } finally {
      setSubmitting(false);
    }
  }

  const onSecondStep = secondFactor !== 'none';

  useEffect(() => {
    onStepChange?.(onSecondStep);
  }, [onSecondStep, onStepChange]);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="login-email">{t('auth.email')}</Label>
        <Input
          id="login-email"
          name="email"
          type="email"
          autoComplete="username"
          required
          readOnly={onSecondStep}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="login-password">{t('auth.secretField')}</Label>
        <Input
          id="login-password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          readOnly={onSecondStep}
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </div>

      {secondFactor === 'totp' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="login-totp">{t('auth.totpCode')}</Label>
          <Input
            id="login-totp"
            name="totpCode"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={totpCode}
            onChange={(event) => {
              setTotpCode(event.target.value);
            }}
          />
          <Button
            type="button"
            variant="link"
            className="self-start px-0"
            onClick={() => {
              setSecondFactor('recovery');
              setTotpCode('');
            }}
          >
            {t('auth.useRecoveryCode')}
          </Button>
        </div>
      )}

      {secondFactor === 'recovery' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="login-recovery">{t('auth.recoveryCode')}</Label>
          <Input
            id="login-recovery"
            name="recoveryCode"
            autoComplete="one-time-code"
            value={recoveryCode}
            onChange={(event) => {
              setRecoveryCode(event.target.value);
            }}
          />
          <Button
            type="button"
            variant="link"
            className="self-start px-0"
            onClick={() => {
              setSecondFactor('totp');
              setRecoveryCode('');
            }}
          >
            {t('auth.useAuthenticator')}
          </Button>
        </div>
      )}

      {failure !== null && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{failure}</AlertDescription>
        </Alert>
      )}

      {remaining !== null && (
        <p className="text-xs text-muted-foreground">
          {t('auth.recoveryCodesRemaining', { remaining })}
        </p>
      )}

      <Button type="submit" disabled={submitting}>
        {t(submitting ? 'auth.signingIn' : 'auth.signIn')}
      </Button>
    </form>
  );
}
